import { useEffect, useMemo, useState } from 'react';
import {
  Typography, Table, Tag, Button, message, Space, Select, DatePicker
} from 'antd';
import type { TableProps } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { supabase, formatDateTime, countFailedItems } from '@ubus/shared';
import type { VehicleInspection } from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';
import InspectionDetailDrawer from '../components/InspectionDetailDrawer';
import { useAdminTableStability } from '../hooks/useAdminTableStability';

const { Text } = Typography;

type InspectionRow = VehicleInspection & {
  drivers?: { name: string };
  vehicles?: {
    fleet_number?: string | null;
    model?: string | null;
    registration_plate?: string | null;
  } | null;
};

function formatInspectionVehicle(row: InspectionRow): string {
  const v = row.vehicles;
  if (v) {
    const plate = v.registration_plate?.trim();
    const fleet = v.fleet_number?.trim();
    if (plate && fleet) return `${plate} (${fleet})`;
    if (plate || fleet) return plate || fleet || '—';
    if (v.model?.trim()) return v.model.trim();
  }
  const rego = row.work_fields?.rego?.trim();
  return rego || '—';
}

type SeverityFilter = 'all' | 'pass' | 'pass_with_notes' | 'blocked';
type AfterFilter = 'all' | 'done' | 'pending';

function VehicleInspections() {
  const { t } = useTranslation();
  const [inspections, setInspections] = useState<InspectionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<InspectionRow | null>(null);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [afterFilter, setAfterFilter] = useState<AfterFilter>('all');
  const [dayFilter, setDayFilter] = useState<dayjs.Dayjs | null>(null);
  const inspectionsTable = useAdminTableStability();

  const fetchInspections = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vehicle_inspections')
        .select('*, drivers(name), vehicles(fleet_number, model, registration_plate)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setInspections((data || []) as InspectionRow[]);
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('operations.inspectionsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchInspections();
  }, []);

  const filteredInspections = useMemo(() => {
    return inspections.filter((row) => {
      if (severityFilter !== 'all' && row.severity_result !== severityFilter) return false;
      if (afterFilter === 'done' && !row.after_completed_at) return false;
      if (afterFilter === 'pending' && row.after_completed_at) return false;
      if (dayFilter) {
        const day = dayFilter.format('YYYY-MM-DD');
        if (row.inspection_date !== day) return false;
      }
      return true;
    });
  }, [inspections, severityFilter, afterFilter, dayFilter]);

  const inspectionColumns: TableProps<InspectionRow>['columns'] = [
    {
      title: t('operations.columns.date'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      fixed: 'left',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('operations.columns.vehicle'),
      key: 'vehicle',
      width: 180,
      ellipsis: true,
      render: (_: unknown, row) => formatInspectionVehicle(row),
    },
    {
      title: t('operations.columns.driver'),
      dataIndex: ['drivers', 'name'],
      key: 'driver',
      width: 140,
      render: (v: string) => v || '—',
    },
    {
      title: t('operations.columns.severity'),
      dataIndex: 'severity_result',
      key: 'severity_result',
      width: 150,
      render: (v: string | null) => {
        if (!v) return '—';
        const color = v === 'blocked' ? 'error' : v === 'pass_with_notes' ? 'warning' : 'success';
        return <Tag color={color}>{t(`operations.severity.${v}`, { defaultValue: v })}</Tag>;
      },
    },
    {
      title: t('operations.columns.declaredAt'),
      dataIndex: 'declaration_accepted_at',
      key: 'declaration_accepted_at',
      width: 160,
      render: (v: string | null) => (v ? formatDateTime(v) : '—'),
    },
    {
      title: t('operations.columns.fails'),
      key: 'fails',
      width: 90,
      render: (_, row) => {
        const n = countFailedItems(row.items);
        return n > 0 ? <Tag color="error">{n}</Tag> : <Text type="secondary">0</Text>;
      },
    },
    {
      title: t('operations.columns.defects'),
      dataIndex: 'body_defects',
      key: 'body_defects',
      width: 90,
      render: (defects: unknown) => (Array.isArray(defects) ? defects.length : 0),
    },
    {
      title: t('operations.columns.afterDone'),
      dataIndex: 'after_completed_at',
      key: 'after_completed_at',
      width: 110,
      render: (v: string | null) =>
        v ? <Tag color="success">{t('operations.yes')}</Tag> : <Tag>{t('operations.no')}</Tag>,
    },
    {
      title: t('operations.columns.notes'),
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
    },
    {
      title: t('operations.columns.actions'),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, row) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => setDetail(row)}>
          {t('operations.view')}
        </Button>
      ),
    },
  ];

  return (
    <AdminListPage
      title={t('operations.inspectionsTitle')}
      subtitle={t('operations.inspectionsSubtitle')}
      resultCount={filteredInspections.length}
      widthMode="fluid"
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          style={{ width: 180 }}
          value={severityFilter}
          onChange={setSeverityFilter}
          options={[
            { value: 'all', label: t('operations.filters.severityAll') },
            { value: 'pass', label: t('operations.severity.pass') },
            { value: 'pass_with_notes', label: t('operations.severity.pass_with_notes') },
            { value: 'blocked', label: t('operations.severity.blocked') },
          ]}
        />
        <Select
          style={{ width: 160 }}
          value={afterFilter}
          onChange={setAfterFilter}
          options={[
            { value: 'all', label: t('operations.filters.afterAll') },
            { value: 'done', label: t('operations.filters.afterDone') },
            { value: 'pending', label: t('operations.filters.afterPending') },
          ]}
        />
        <DatePicker
          value={dayFilter}
          onChange={setDayFilter}
          placeholder={t('operations.filters.day')}
          allowClear
        />
      </Space>
      <Table
        dataSource={filteredInspections}
        columns={inspectionColumns}
        rowKey="id"
        loading={loading}
        pagination={inspectionsTable.pagination}
        onChange={inspectionsTable.onTableChange}
        scroll={{ x: 1200 }}
        locale={{ emptyText: t('operations.empty.inspections') }}
        onRow={(row) => ({
          onClick: () => setDetail(row),
          style: { cursor: 'pointer' },
        })}
      />
      <InspectionDetailDrawer
        open={!!detail}
        record={detail}
        onClose={() => setDetail(null)}
      />
    </AdminListPage>
  );
}

export default VehicleInspections;
